import React, { useState, useEffect, useCallback } from 'react';
import Icon from 'components/AppIcon';
import api from '../../../lib/api';

export default function AttendancePanel() {
  const [mode, setMode] = useState('manual');
  const [memberId, setMemberId] = useState('');
  const [scanActive, setScanActive] = useState(false);
  const [checkedIn, setCheckedIn] = useState(null);
  const [attendances, setAttendances] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkInLoading, setCheckInLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ checkedInToday: 0, activeSessions: 0, pendingCheckouts: 0 });

  const fetchTodayAttendances = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/attendances/today');
      const records = data || [];
      setAttendances(records);
      const checkedInToday = records?.length;
      const activeSessions = records?.filter(r => !r?.check_out_time)?.length;
      setStats({ checkedInToday, activeSessions, pendingCheckouts: activeSessions });
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMembers = useCallback(async () => {
    try {
      const { data } = await api.get('/members', { params: { status: 'active', limit: 2000 } });
      setMembers(data || []);
    } catch (err) {
      console.error('Failed to fetch members:', err);
    }
  }, []);

  useEffect(() => {
    fetchTodayAttendances();
    fetchMembers();
  }, [fetchTodayAttendances, fetchMembers]);

  const doCheckIn = async (type, memberIdValue) => {
    setCheckInLoading(true);
    try {
      let targetMember = members?.find(m => m?.id === memberIdValue);
      if (!targetMember && memberIdValue) {
        const searchLower = memberIdValue?.toLowerCase();
        targetMember = members?.find(m =>
          `${m?.first_name} ${m?.last_name}`?.toLowerCase()?.includes(searchLower) ||
          m?.id?.toLowerCase()?.startsWith(searchLower)
        );
      }
      if (!targetMember && members?.length > 0) {
        targetMember = members?.[0]; // fallback for QR demo
      }
      if (!targetMember) throw new Error('Member not found');

      const now = new Date();
      await api.post('/attendances', {
        member_id: targetMember?.id,
        check_in_time: now?.toISOString(),
        check_in_type: type,
      });

      setCheckedIn({
        name: `${targetMember?.first_name} ${targetMember?.last_name}`,
        time: now?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      });
      setTimeout(() => setCheckedIn(null), 3000);
      fetchTodayAttendances();
    } catch (err) {
      setError(err?.message);
      setTimeout(() => setError(null), 4000);
    } finally {
      setCheckInLoading(false);
    }
  };

  const handleManualCheckIn = async (e) => {
    e?.preventDefault();
    if (!memberId?.trim()) return;
    await doCheckIn('manual', memberId?.trim());
    setMemberId('');
  };

  const handleCheckOut = async (attendanceId) => {
    try {
      await api.patch(`/attendances/${attendanceId}`, { check_out_time: new Date()?.toISOString() });
      fetchTodayAttendances();
    } catch (err) {
      console.error('Check-out failed:', err);
    }
  };

  const handleQRScan = async () => {
    setScanActive(true);
    setTimeout(async () => {
      setScanActive(false);
      await doCheckIn('qr', null);
    }, 1500);
  };

  const formatTime = (ts) => {
    if (!ts) return '—';
    return new Date(ts)?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const getDuration = (checkIn, checkOut) => {
    if (!checkOut) return 'Active';
    const diff = Math.round((new Date(checkOut) - new Date(checkIn)) / 60000);
    if (diff < 60) return `${diff}m`;
    return `${Math.floor(diff / 60)}h ${diff % 60}m`;
  };

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-5 shadow-[var(--shadow-sm)] h-full flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-[var(--color-foreground)] font-[var(--font-heading)]">Attendance Check-In</h3>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-[var(--color-success)] animate-pulse"></span>
          <span className="text-xs text-[var(--color-success)] font-[var(--font-caption)] font-medium">Live</span>
        </div>
      </div>
      {/* Mode Toggle */}
      <div className="flex bg-[var(--color-muted)] rounded-[var(--radius-sm)] p-1 gap-1">
        {['manual', 'qr']?.map((m) => (
          <button
            key={m}
            onClick={() => setMode(m)}
            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded text-xs font-medium font-[var(--font-caption)] transition-all duration-200 ${
              mode === m ? 'bg-[var(--color-card)] text-[var(--color-foreground)] shadow-sm' : 'text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]'
            }`}
          >
            <Icon name={m === 'manual' ? 'Keyboard' : 'QrCode'} size={14} color="currentColor" />
            {m === 'manual' ? 'Manual Entry' : 'QR Scan'}
          </button>
        ))}
      </div>
      {/* Error Toast */}
      {error && (
        <div className="flex items-center gap-3 bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 rounded-[var(--radius-sm)] px-4 py-3">
          <Icon name="AlertCircle" size={18} color="var(--color-error)" />
          <p className="text-sm font-medium text-[var(--color-error)] font-[var(--font-caption)]">{error}</p>
        </div>
      )}
      {/* Success Toast */}
      {checkedIn && (
        <div className="flex items-center gap-3 bg-[var(--color-success)]/10 border border-[var(--color-success)]/20 rounded-[var(--radius-sm)] px-4 py-3">
          <Icon name="CheckCircle" size={18} color="var(--color-success)" />
          <div>
            <p className="text-sm font-semibold text-[var(--color-success)] font-[var(--font-caption)]">{checkedIn?.name} checked in</p>
            <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{checkedIn?.time}</p>
          </div>
        </div>
      )}
      {/* Manual Entry Form */}
      {mode === 'manual' && (
        <form onSubmit={handleManualCheckIn} className="flex flex-col gap-3">
          <div>
            <label className="block text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-1.5">Member Name or ID</label>
            <input
              type="text"
              value={memberId}
              onChange={(e) => setMemberId(e?.target?.value)}
              placeholder="Enter member name..."
              list="member-suggestions"
              className="w-full px-3 py-2.5 bg-[var(--color-background)] border border-[var(--color-border)] rounded-[var(--radius-sm)] text-sm text-[var(--color-foreground)] font-[var(--font-caption)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
            />
            <datalist id="member-suggestions">
              {members?.map(m => (
                <option key={m?.id} value={`${m?.first_name} ${m?.last_name}`} />
              ))}
            </datalist>
          </div>
          <button
            type="submit"
            disabled={checkInLoading || !memberId?.trim()}
            className="flex items-center justify-center gap-2 py-2.5 bg-[var(--color-primary)] text-white rounded-[var(--radius-sm)] text-sm font-medium font-[var(--font-caption)] hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {checkInLoading ? <Icon name="Loader2" size={14} color="#fff" className="animate-spin" /> : <Icon name="LogIn" size={14} color="#fff" />}
            {checkInLoading ? 'Checking In...' : 'Check In'}
          </button>
        </form>
      )}
      {/* QR Scanner */}
      {mode === 'qr' && (
        <div className="flex flex-col items-center gap-4">
          <div
            onClick={!checkInLoading ? handleQRScan : undefined}
            className={`w-full h-36 rounded-[var(--radius-md)] border-2 border-dashed flex flex-col items-center justify-center gap-3 cursor-pointer transition-all duration-300 ${
              scanActive ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5 animate-pulse' : 'border-[var(--color-border)] hover:border-[var(--color-primary)]/50 hover:bg-[var(--color-muted)]'
            }`}
          >
            <Icon name="QrCode" size={40} color={scanActive ? 'var(--color-primary)' : 'var(--color-muted-foreground)'} />
            <p className="text-sm font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
              {scanActive ? 'Scanning...' : 'Click to Simulate QR Scan'}
            </p>
          </div>
        </div>
      )}
      {/* Today Stats */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: 'Today', value: stats?.checkedInToday, color: 'var(--color-primary)' },
          { label: 'Active', value: stats?.activeSessions, color: 'var(--color-success)' },
          { label: 'Pending Out', value: stats?.pendingCheckouts, color: 'var(--color-warning)' },
        ]?.map(s => (
          <div key={s?.label} className="bg-[var(--color-background)] border border-[var(--color-border)] rounded-[var(--radius-sm)] p-2 text-center">
            <p className="text-lg font-bold font-[var(--font-heading)]" style={{ color: s?.color }}>{s?.value}</p>
            <p className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{s?.label}</p>
          </div>
        ))}
      </div>
      {/* Recent Check-Ins */}
      <div className="flex-1 overflow-hidden">
        <p className="text-xs font-semibold text-[var(--color-muted-foreground)] font-[var(--font-caption)] uppercase tracking-wider mb-3">Today's Activity</p>
        {loading ? (
          <div className="flex flex-col gap-2">
            {[1, 2, 3]?.map(i => (
              <div key={i} className="h-12 bg-[var(--color-muted)] rounded animate-pulse" />
            ))}
          </div>
        ) : attendances?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <Icon name="UserCheck" size={28} color="var(--color-muted-foreground)" />
            <p className="mt-2 text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">No check-ins today</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2 overflow-y-auto max-h-52">
            {attendances?.map((item) => {
              const name = item?.member_name || 'Unknown';
              const initials = name?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2);
              const isActive = !item?.check_out_time;
              return (
                <div key={item?.id} className="flex items-center gap-3 py-2 border-b border-[var(--color-border)] last:border-0">
                  <div className="w-8 h-8 rounded-full bg-[var(--color-primary)]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-[var(--color-primary)] font-[var(--font-caption)]">{initials}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[var(--color-foreground)] font-[var(--font-caption)] truncate">{name}</p>
                    <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                      {formatTime(item?.check_in_time)} → {formatTime(item?.check_out_time)} · {getDuration(item?.check_in_time, item?.check_out_time)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full font-[var(--font-caption)] ${
                      isActive ? 'bg-[var(--color-success)]/10 text-[var(--color-success)]' : 'bg-[var(--color-muted)] text-[var(--color-muted-foreground)]'
                    }`}>
                      {isActive ? 'In' : 'Out'}
                    </span>
                    {isActive && (
                      <button
                        onClick={() => handleCheckOut(item?.id)}
                        className="p-1 rounded hover:bg-[var(--color-error)]/10 transition-colors"
                        title="Check Out"
                      >
                        <Icon name="LogOut" size={12} color="var(--color-error)" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
